import { makeAutoObservable, runInAction } from 'mobx'
import { nanoid } from 'nanoid'
import { AxiosError } from 'axios'
import { CHATBOT_NAME_LIMIT } from 'shared/constants/limits'
import { uiStore } from 'shared/store/uiStore'
import { showToast } from 'shared/ui'
import { logger } from 'shared/lib'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { ModalTypeList } from 'shared/ui/Modal/store/types'
import { BlockerStore } from 'shared/ui/Blocker'
import { type INormalizedApiError } from 'shared/api'
import {
  type IAICallingType,
  type ICallingActionPayload,
  type IParamsCreateAICallingAgent,
  type IResponseFullAICallingAgent,
  type IResponseTemplate,
} from 'entities/AICalling/api/types'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { AiCallingApi } from 'entities/AICalling/api/aiCallingApi'
import { showAIAgentDisabledToast } from 'entities/AIAgent/actions/showAIAgentDisabledToast'
import { NameInputStore } from 'features/CreateOrEdit/NameInput'
import { GreetingStore } from 'pages/chatbot/pages/calling/ui/AICallingAgentManage/ui/Greeting/store/GreetingStore'
import { ChatbotPath } from 'pages/chatbot/route/chatbotPath'
import { AgentInstructionsStore } from 'pages/chatbot/pages/calling/ui/AICallingAgentManage/ui/AgentInstructions/store/AgentInstructionsStore'
import { AIKnowledgeBaseStore } from 'pages/chatbot/pages/calling/ui/AICallingAgentManage/ui/AIKnowledgeBase/store/AIKnowledgeBaseStore'
import { TestAgentStore } from 'pages/chatbot/pages/calling/ui/AICallingAgentManage/ui/TestAgent/store/TestAgentStore'
import { showSuccessActivatedModal } from 'pages/chatbot/pages/calling/actions/showSuccessActivatedModal/showSuccessActivatedModal'
import { disableAgent } from 'pages/chatbot/pages/calling/actions/disableAgent/disableAgent'

export class AICallingAgentManageStore {
  readonly nameInputStore: NameInputStore
  readonly blockerStore: BlockerStore
  readonly greetingStore: GreetingStore
  readonly aiKnowledgeBaseStore: AIKnowledgeBaseStore
  readonly agentInstructionsStore: AgentInstructionsStore
  readonly templatesMap: Map<IAICallingType, IResponseTemplate>
  readonly testAgentStore: TestAgentStore

  private _onSuccessSaved?: (
    data: IResponseFullAICallingAgent,
    options: { isForTest?: boolean }
  ) => void | Promise<void>

  constructor(
    readonly entities: IEntitiesStore,
    options: {
      templates: IResponseTemplate[]
      response?: IResponseFullAICallingAgent | null
      isDuplicate?: boolean
      type?: IAICallingType
      testAgentId?: number
      initialInstruction?: string
      initialStatus?: 'active' | 'disabled' | 'draft'
      onSuccessSaved?: (data: IResponseFullAICallingAgent) => void | Promise<void>
    }
  ) {
    this._onSuccessSaved = options.onSuccessSaved
    if (options?.initialStatus) {
      this._initialStatus = options.initialStatus
    }
    this.templatesMap = new Map<IAICallingType, IResponseTemplate>(
      options.templates.map((item) => [item.id, item])
    )
    const response = options?.response
    this._response = response || null

    if (options?.isDuplicate) {
      this.isDuplicate = options.isDuplicate
    }

    this.nameInputStore = new NameInputStore({
      name: response?.name,
      focusOnMount: !response || options?.isDuplicate,
      limit: CHATBOT_NAME_LIMIT,
      isResetErrorOnChange: true,
    })

    if (response?.template_type) {
      this._type = response.template_type
    } else if (options?.type) {
      this._type = options.type
    }

    this.greetingStore = new GreetingStore(entities, this)
    this.aiKnowledgeBaseStore = new AIKnowledgeBaseStore(this)
    this.agentInstructionsStore = new AgentInstructionsStore(
      this.entities,
      this,
      options.initialInstruction
    )

    this.testAgentStore = new TestAgentStore(options.testAgentId)
    response?.actions.forEach((action) => {
      if (action.settings.token_id) {
        this.actionsMap.set(action.settings.token_id, action)
      }
    })

    this.blockerStore = new BlockerStore(this, {
      title: 'Save agent before closing?',
      primaryText: this.status === 'draft' ? 'Save as draft' : 'Save changes',
      primaryAction: this.onSaveBlocker,
    })

    makeAutoObservable(this)
  }

  _response: IResponseFullAICallingAgent | null = null
  get response() {
    return this._response
  }
  isDuplicate = false
  private readonly _initialStatus: 'active' | 'disabled' | 'draft' = 'draft'

  get params(): IParamsCreateAICallingAgent | null {
    if (!this.entities.usersStore.user?.id) return null
    if (!this.entities.organizationStore.id) return null
    if (!this.greetingStore.params) return null
    return {
      name: this.nameInputStore.name,
      user_id: this.entities.usersStore.user?.id,
      organization_id: this.entities.organizationStore.id,
      status: this.status,
      template_type: this.type,
      ...this.greetingStore.params,
      ...this.aiKnowledgeBaseStore.params,
      instructions: this.agentInstructionsStore.instructions,
      actions: this.actions.filter(this.agentInstructionsStore.filterActions),
    }
  }

  get isEdit() {
    return !!this.response && !this.isDuplicate
  }

  get disabledSave() {
    return this.isEdit && !this.blockerStore.isExistChanges
  }

  get template() {
    return this.templatesMap.get(this.type)
  }

  get status() {
    return this.response?.status || this._initialStatus
  }

  private _onSave = async (params: IParamsCreateAICallingAgent, withError?: boolean) => {
    try {
      const { data } =
        this.isEdit && this.response?.id
          ? await AiCallingApi.update(this.response?.id, params)
          : await AiCallingApi.create(params)

      runInAction(() => {
        this._response = data
      })

      this.blockerStore.updateCache()
      return data
    } catch (e) {
      logger.error(e)
      if (e instanceof AxiosError) {
        this._handleError(e.errors)
        if (withError) throw e
      }
    }
  }

  private _handleError = (errors: INormalizedApiError) => {
    errors.entries().forEach(([key, msg]) => {
      if (key === 'name') {
        this.nameInputStore.setError(msg.value)
        return
      }
      if (key === 'knowledge_base_documents') {
        this.aiKnowledgeBaseStore.setKnowledgeBaseDocumentsError(msg.value)
        return
      }
      showToast({ type: 'error', title: msg.value })
    })
  }

  checkValidateForCreate = () => this._checkValidateDraft()

  onSave = async (options?: { isForTest?: boolean }) => {
    const isEdit = this.isEdit
    const isForTest = options?.isForTest
    const isValid =
      this.status === 'draft' && !isForTest ? this._checkValidateDraft() : this._checkValidate()
    if (isValid && this.params) {
      const data = await this._onSave(this.params, true)
      if (!data) {
        return
      }

      if (this._onSuccessSaved) {
        await this._onSuccessSaved(data, { isForTest: options?.isForTest })
      }
      if (isEdit) {
        showToast({
          type: 'info',
          title: 'Changes saved',
        })
      } else {
        if (!this._onSuccessSaved) {
          uiStore.changeRoute({
            path: ChatbotPath.callingEdit(data.id),
            options: {
              state: { response: data, testAgentId: isForTest ? data.id : undefined },
            },
          })
        }

        showToast({
          type: 'info',
          title: 'AI Agent saved',
        })
      }
      return data
    }
  }

  onPublish = async () => {
    const isValid = this._checkValidate()
    if (isValid && this.params) {
      const data = await this._onSave({ ...this.params, status: 'active' })
      if (!data) {
        return
      }
      this.blockerStore.updateCache()
      uiStore.changeRoute({
        path: ChatbotPath.calling,
      })
      showSuccessActivatedModal()
    }
  }

  onDisable = async () => {
    if (!this.response?.id) return
    if (!this.params) return
    const isValid = this._checkValidate()
    if (!isValid) return

    await disableAgent({
      id: this.response.id,
      disableRequest: async () => {
        if (!this.params) return
        try {
          const data = await this._onSave({ ...this.params, status: 'disabled' })
          if (!data) {
            return
          }
          this.blockerStore.updateCache()
          showAIAgentDisabledToast()
        } catch (e) {
          logger.error(e)
        }
      },
    })
  }

  onSaveBlocker = async () => {
    const isValid = this.status === 'draft' ? this._checkValidateDraft() : this._checkValidate()
    if (isValid && this.params) {
      await this._onSave(this.params, true)
    } else {
      throw new Error('Validation failed')
    }
  }

  private _checkValidate = () => {
    let validate = true
    if (!this.nameInputStore.checkValidate('Add a name for your agent')) {
      validate = false
    }
    if (!this.greetingStore.checkValidate()) {
      validate = false
    }
    if (!this.aiKnowledgeBaseStore.checkValidate()) {
      validate = false
    }
    if (!this.agentInstructionsStore.checkValidate()) {
      validate = false
    }
    return validate
  }
  private _checkValidateDraft = () => {
    let validate = true
    if (!this.nameInputStore.checkValidate('Add a name for your agent')) {
      validate = false
    }
    return validate
  }

  private _type: IAICallingType = 'faq_information'
  get type() {
    return this._type
  }

  setType = (value: typeof this.type) => {
    const modalId = nanoid()
    modalStore.addModal({
      type: ModalTypeList.WARNING,
      id: modalId,
      title: 'Change agent type?',
      desc: 'Changing the agent type will reset agent instructions and the greeting message',
      width: 280,
      primaryAction: {
        text: 'Yes, change',
        onAction: async () => {
          this._type = value
          modalStore.closeModal(modalId)
        },
      },
      secondaryAction: {
        text: 'Cancel',
        onAction: () => {
          modalStore.closeModal(modalId)
        },
      },
    })
  }

  actionsMap: Map<string, ICallingActionPayload> = new Map()
  get actions() {
    return Array.from(this.actionsMap.values())
  }
}
